import { useEffect, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import withReactContent from "sweetalert2-react-content";
import { fetchIncident } from "../../redux/incidentSlice";
import { useDispatch, useSelector } from "react-redux";

function Incidents() {
  const generateRandomId = () => Math.floor(100000 + Math.random() * 900000);

  const [formData, setFormData] = useState({
    incidentId: generateRandomId().toString(),
    title: "",
    points: "",
    description: "",
  });

  const dispatch = useDispatch();
  const { data: incident, loading: fetchLoading } = useSelector(
    (state) => state.incident
  );

  useEffect(() => {
    dispatch(fetchIncident());
  }, [dispatch]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const MySwal = withReactContent(Swal);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [editMode, setEditMode] = useState(false);

  const totalFields = Object.keys(formData).length;
  const filledFields = Object.values(formData).filter((val) =>
    typeof val === "string" ? val.trim() !== "" : val.length > 0
  ).length;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: false }));
  };

  const resetForm = () => {
    setFormData({
      incidentId: generateRandomId().toString(),
      title: "",
      points: "",
      description: "",
    });
    setEditMode(false);
    setError({});
  };

  const handleEditClick = (incident) => {
    setFormData({
      incidentId: incident.incidentId,
      title: incident.title,
      points: incident.points,
      description: incident.description,
    });
    setEditMode(true);

    const modal = new window.bootstrap.Modal(
      document.querySelector(".gd-example-modal-lg")
    );
    modal.show();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (!formData[key].toString().trim()) newErrors[key] = true;
    });

    if (Object.keys(newErrors).length > 0) {
      setError(newErrors);
      return;
    }

    setLoading(true);

    try {
      const url = editMode
        ? `${server}/update_incident_mongodb`
        : `${server}/add_incident_mongodb`;

      await axios.post(url, formData, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });

      toast.success(editMode ? "Incident Updated" : "Incident Added", {
        position: "top-right",
      });

      const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
      if (closeButton) closeButton.click();

      fetchIncident();

      setFormData({
        incidentId: generateRandomId().toString(),
        title: "",
        points: "",
        description: "",
      });
      setEditMode(false);
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      toast.error("Please Try Again", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  // Filter
  const filteredRecords = incident.filter((incident) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(incident || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower)
    );
  });

  // Calculate pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleDelete = async (incidentIdToDelete) => {
    const result = await MySwal.fire({
      title: "Are you sure?",
      text: `Do you want to delete incident ID "${incidentIdToDelete}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.post(
          `${server}/delete_incident_mongodb`,
          { incidentId: incidentIdToDelete },
          {
            headers: {
              Authorization: `${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (response.status === 200) {
          toast.success("Incident Deleted", { position: "top-right" });
          fetchIncident();
        } else {
          toast.error("Deletion failed. Please try again.");
        }
      } catch (error) {
        toast.error("Error occurred while deleting. Try again.");
      }
    }
  };

  const handleExportIncidentExcel = () => {
    if (!incident || incident.length === 0) {
      toast.error("No data to export");
      return;
    }

    const sheetData = incident.map((item) => ({
      "Incident ID": item?.incidentId || "",
      Title: item?.title || "",
      Points: item?.points || "",
      Description: item?.description || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Incident Report");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `IncidentReport_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportNagativeIncidentExcel = () => {
    if (!incident || incident.length === 0) {
      toast.error("No data to export");
      return;
    }

    const negativeIncidents = incident.filter(
      (item) => Number(item?.points) < 0
    );

    if (negativeIncidents.length === 0) {
      toast.warning("No negative incidents to export");
      return;
    }

    const sheetData = negativeIncidents.map((item) => ({
      "Incident ID": item?.incidentId || "",
      Title: item?.title || "",
      Points: item?.points || "",
      Description: item?.description || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Negative Incidents");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `NegativeIncident_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportPositiveIncidentExcel = () => {
    if (!incident || incident.length === 0) {
      toast.error("No data to export");
      return;
    }

    const positiveIncidents = incident.filter(
      (item) => Number(item?.points) > 0
    );

    if (positiveIncidents.length === 0) {
      toast.warning("No positive incidents to export");
      return;
    }

    const sheetData = positiveIncidents.map((item) => ({
      "Incident ID": item?.incidentId || "",
      Title: item?.title || "",
      Points: item?.points || "",
      Description: item?.description || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Positive Incidents");

    const today = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `PositiveIncident_${today}.xlsx`);
    toast.success("Report Generated");
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div
            class="modal fade gd-example-modal-lg"
            tabindex="-1"
            role="dialog"
            aria-labelledby="myLargeModalLabel"
            aria-hidden="true"
          >
            <div class="modal-dialog modal-lg">
              <div class="modal-content">
                <form onSubmit={handleSubmit}>
                  <div class="modal-header d-flex justify-content-between">
                    <h5 class="modal-title fs-4" id="exampleModalToggleLabel">
                      Add Incident{" "}
                      <span className="ms-3 text-secondary">
                        ({String(filledFields).padStart(2, "0")}/
                        {String(totalFields).padStart(2, "0")})
                      </span>
                    </h5>
                    <div className="d-flex align-items-center">
                      <span class="badge bg-success-soft me-3 px-3">
                        Editable
                      </span>
                      <div class="form-check form-switch me-4">
                        <input
                          type="checkbox"
                          class="form-check-input m-0"
                          checked
                        />
                      </div>
                      <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      ></button>
                    </div>
                  </div>

                  <div class="modal-body">
                    <div className="row g-2 mb-2">
                      <div className="col-lg-6 col-12">
                        <label class="form-label mb-1 ms-3">
                          Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control text-dark ${
                            error.title ? "border-danger" : ""
                          }`}
                          placeholder="Enter Title"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-lg-6 col-12">
                        <label class="form-label mb-1 ms-3">
                          Points <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control text-dark ${
                            error.points ? "border-danger" : ""
                          }`}
                          placeholder="Enter Points"
                          name="points"
                          value={formData.points}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-12">
                        <label class="form-label mb-1 ms-3">
                          Description <span className="text-danger">*</span>
                        </label>
                        <textarea
                          type="text"
                          className={`form-control text-dark ${
                            error.description ? "border-danger" : ""
                          }`}
                          rows="3"
                          placeholder="Enter Description"
                          name="description"
                          value={formData.description}
                          onChange={handleChange}
                          required
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  <div class="modal-footer d-flex justify-content-between py-2">
                    <div>
                      <button
                        type="button"
                        class="btn btn-secondary px-3 me-1 rounded-pill"
                        data-bs-dismiss="modal"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                      <button
                        type="submit"
                        class="btn btn-primary px-5 rounded-pill fw-normal"
                        disabled={loading}
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords.map((incident, index) => (
                        <tr key={index}>
                          <td>{indexOfFirstRecord + index + 1}.</td>
                          <td className="lh-sm py-4 text-wrap">
                            <span class="text-dark">Incident ID</span> &mdash;{" "}
                            {incident.incidentId}
                            <br />
                            <span class="text-dark">Title</span> &mdash;{" "}
                            <span className="text-capitalize">
                              {incident.title}
                            </span>
                            <br />
                            <span class="text-dark">Points</span> &mdash;{" "}
                            {incident.points}
                            <br />
                            <span class="text-dark">
                              Description
                            </span> &mdash;{" "}
                            {incident.description
                              .split(" ")
                              .map((word, index) => (
                                <span key={index}>
                                  {word} {(index + 1) % 4 === 0 ? <br /> : " "}
                                </span>
                              ))}
                          </td>
                          <td>
                            <div className="d-flex flex-column gap-1 width-fit-content">
                              <span
                                onClick={() => handleEditClick(incident)}
                                class="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                              >
                                Update
                              </span>
                              <span
                                onClick={() =>
                                  handleDelete(incident.incidentId)
                                }
                                class="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                              >
                                Delete
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="d-flex bg-white justify-content-between rounded-4">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                      <Link
                        onClick={resetForm}
                        data-bs-toggle="modal"
                        data-bs-target=".gd-example-modal-lg"
                        class="btn btn-warning px-5 fs-6 fw-medium rounded-4 fit-content"
                      >
                        Add Incident
                      </Link>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Behaviour Records</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Incident
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {fetchLoading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Incident
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            0.2 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span
                                onClick={handleExportIncidentExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Incident Report
                              </span>
                              <span
                                onClick={handleExportNagativeIncidentExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Nagative Incidents
                              </span>
                              <span
                                onClick={handleExportPositiveIncidentExcel}
                                class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                              >
                                Positive Incident Report
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default Incidents;
